import styled from 'styled-components';

import { Skeleton } from '@/ui/Skeleton';
import { makeNamespacedComponent } from '@/ui/utils';
import { Icon } from '@/ui/Icon';
import { formatToHoursAndMinutesFromSeconds } from '@/utils/time';
import { Button } from '@/ui/Button';
import { LinearProgressBar } from '@/ui/ProgressBar';
import { utcToLocalDateСustomFormat } from '@/utils/time';
import { useLanguages } from '@/store/languages';
import { Progress } from '@/administration/pages/Course/store/progress/types';

const RootStyled = styled.div`
  padding: 32px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.outline1};
`;

const Title = styled.h3`
  ${(p) => p.theme.typography.title3}
  color: ${(p) => p.theme.colors.neutral.onBackground};
  margin: 0px 0 24px 0;
`;

const TitleSkeleton = styled(Skeleton)`
  margin: 0px 0 24px 0;
`;
const BodySkeleton = styled(Skeleton)`
  margin: 0px 0 16px 0;
`;

const Subtitle = styled.div`
  ${(p) => p.theme.typography.subheadline}
  color: ${(p) => p.theme.colors.neutral.onContainer};
  margin: 16px 0 0px 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const ButtonWrapper = styled.div`
  margin: 32px 0 0 0;
`;

const ButtonStyled = styled(Button)`
  width: 100%;
  gap: 10px;
`;

type StartProgressOverviewProps = {
  course: {
    effortHours: number;
    effortMinutes: number;
    language?: number;
    level?: string;
  };
  progress?: Progress;
  startedHeadline: string;
  startLoading: boolean;
  onStart: () => void;
  onRevisit: () => void;
  onContinue: () => void;
};

const StartProgressOverview = ({
  course,
  progress,
  startLoading,
  onStart,
  onContinue,
  onRevisit,
  startedHeadline,
}: StartProgressOverviewProps): JSX.Element => {
  const { languages } = useLanguages();

  const effortSeconds = course.effortHours * 60 * 60 + course.effortMinutes * 60;
  const completedPercentage = progress?.completedPercentage || 0;

  const languageName = languages.find((language) => language.id === course.language)?.name;

  return (
    <RootStyled>
      <Title>{progress?.isStarted ? startedHeadline : 'Overview'}</Title>
      {progress?.isStarted && (
        <LinearProgressBar
          size="lg"
          color="gradient"
          value={completedPercentage}
          maxValue={100}
          roundedBar={false}
          labelFormat="percentage"
          roundedTrack
          showLabel
        />
      )}
      {progress?.isCompleted && progress?.completedAt && (
        <Subtitle>
          <Icon icon={['fas', 'check']} size="sm" />
          Completed on {utcToLocalDateСustomFormat(progress.completedAt, 'YYYY-MM-DD')}
        </Subtitle>
      )}
      <Subtitle>
        <Icon icon={['far', 'clock']} size="sm" />
        {!progress?.isCompleted &&
          `${formatToHoursAndMinutesFromSeconds(((100 - completedPercentage) * effortSeconds) / 100)} left`}
        {progress?.isCompleted && `${formatToHoursAndMinutesFromSeconds(effortSeconds)} total`}
      </Subtitle>
      {course.level && (
        <Subtitle>
          <Icon icon={['fas', 'signal-alt']} size="sm" />
          {course.level}
        </Subtitle>
      )}
      {languageName && (
        <Subtitle>
          <Icon icon={['far', 'language']} size="sm" />
          {languageName}
        </Subtitle>
      )}
      <ButtonWrapper>
        {!progress?.isStarted && (
          <ButtonStyled variant="primary" onClick={onStart} size="large" disabled={startLoading} $loading={startLoading}>
            <Icon icon={['far', 'play']} size="sm" />
            Start
          </ButtonStyled>
        )}
        {progress?.isStarted && !progress?.isCompleted && (
          <ButtonStyled variant="primary" onClick={onContinue} size="large">
            <Icon icon={['far', 'play']} size="sm" />
            Continue
          </ButtonStyled>
        )}
        {progress?.isStarted && progress?.isCompleted && (
          <ButtonStyled variant="secondary" onClick={onRevisit} size="large">
            Revisit
          </ButtonStyled>
        )}
      </ButtonWrapper>
    </RootStyled>
  );
};

const LoadingState = () => (
  <RootStyled>
    <TitleSkeleton height={24} width={186} />
    <BodySkeleton height={24} />
    <BodySkeleton height={24} />
    <BodySkeleton height={24} />
  </RootStyled>
);

const _StartProgressOverview = makeNamespacedComponent(StartProgressOverview, {
  LoadingState,
});

export { _StartProgressOverview as StartProgressOverview };
